import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "chat-messages-panel__messages-reply"
}
const _hoisted_2 = { class: "chat-messages-panel__messages-reply-count" }
const _hoisted_3 = { class: "chat-messages-panel__messages-reply-btn" }
const _hoisted_4 = {
  key: 1,
  class: "chat-messages-panel__pagination-loader chat-messages-panel__pagination-loader--top"
}
const _hoisted_5 = {
  key: 2,
  class: "chat-messages-panel__pagination-loader chat-messages-panel__pagination-loader--bottom"
}
const _hoisted_6 = { class: "chat-messages-panel__input" }
const _hoisted_7 = {
  key: 0,
  class: "chat-messages-panel__input-answered-message"
}
const _hoisted_8 = { class: "answered-message__body" }
const _hoisted_9 = { class: "answered-message__body-header" }
const _hoisted_10 = { class: "answered-message__body-header-name" }
const _hoisted_11 = { class: "answered-message__body-header-date" }
const _hoisted_12 = ["innerHTML"]
const _hoisted_13 = {
  key: 1,
  class: "chat-messages-panel__input-answered-message"
}
const _hoisted_14 = { class: "answered-message__body" }
const _hoisted_15 = { class: "answered-message__body-header" }
const _hoisted_16 = { class: "answered-message__body-text" }
const _hoisted_17 = { class: "chat-messages-panel__input-field" }

import {
	computed,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch
} from "vue";
import {
	ChatMessage,
	ChatModes
} from "@/store/chats";
import { headerStore } from "@/store/header";
import { workerStore } from "@/store/worker";
import moment from "moment/moment";
import AppLoader from "@/components/common/AppLoader.vue";
import ChatMessageComponent from "@/components/main/chat-panel/ChatMessage.vue";
import AppCustomTextarea from "@/components/ui-kit/textarea/AppCustomTextarea.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import HeaderChatResendModal from "@/components/layout/AppHeader/chat-panel/modals/HeaderChatResendModal.vue";
import BillInfoModal from "@/components/main/client-block-page/modals/BillInfoModal.vue";
import { applyChatMessagePlaceholders } from "@/utils/textHelpers";
import { i18n } from "@/i18n";

import { useChatInputFocus } from "@/composables/useChatInputFocus";
import { useChatKeyboardViewport } from "@/composables/useChatKeyboardViewport";
import { useChatMessageLinks } from "@/composables/useChatMessageLinks";
import { debounce } from "lodash";


export default /*@__PURE__*/_defineComponent({
  __name: 'HeaderChatMessagesPanel',
  props: {
    mode: {}
  },
  emits: ["on-open-message", "on-navigate"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const t = (key: string): string => String(i18n.global.t(key))
const sendMessageTypes = computed(() => [
	{
		id: 0,
		sendMessageText: t('chat_send_hint_ctrl_enter')
	},
	{
		id: 1,
		sendMessageText: t('chat_send_hint_enter')
	},
])

const chatStore = headerStore()
const wStore = workerStore()
const props = __props
const emit = __emit

const $resendMessages = ref<typeof HeaderChatResendModal | null>(null)
const $billInfoModal = ref<InstanceType<typeof BillInfoModal> | null>(null)
const { inputRef, focusSync, focusAsync } = useChatInputFocus()

const openBillInfo = (billId: number) => $billInfoModal.value?.onOpen(billId)
const { onMessageClick } = useChatMessageLinks({
	onOpenBill: openBillInfo,
	onNavigated: () => emit('on-navigate'),
})
const $messagesArea = ref<HTMLDivElement | null>(null)
const $chatPanel = ref<HTMLElement | null>(null)
const {
	isInputFocused,
	panelStyle: keyboardPanelStyle,
	onInputFocus: onChatInputFocus,
	onInputBlur: onChatInputBlur,
	scrollToLatest,
} = useChatKeyboardViewport($chatPanel, $messagesArea)
const message = ref<string>("")
const selectedMessages = ref<number[]>([])
const isScrollBtnVisible = ref(false)
const isFirstLoading = ref(true)
const isFirstLoadingFromSearch = ref(true)
const currentChatScrollTopPosition = ref<number | null>(null)

const worker = computed(() => wStore.workerProfile)
const isActiveMode = computed(() => props.mode === 'chat')
const chat = computed(() => chatStore.getOpenedChat)
const chatLoadingState = computed(() => chatStore.getOpenedChatLoadState)
const isLoadingChat = computed(() => chatStore.isChatMessagesLoading)
const isLoadingPrev = computed(() => chatStore.loadingPrev)
const isLoadingNext = computed(() => chatStore.loadingNext)
const chatMessages = computed(() =>
	props.mode === 'chat'
		? chatStore.getOpenedChatMessages
		: chatStore.getSearchedMessages
)
const chatMessagesHistory = computed(() => chatStore.getOpenedChatsHistory)
const resendMessagesList = computed(() => chatStore.resendMessages)
const workerSendMessageType = computed(() => {
	if (!worker.value) return sendMessageTypes.value[0]

	return sendMessageTypes.value.find(type => type.id === worker.value?.MessageSendingWay)
})

const attachOrResendMessages = computed(() =>
	resendMessagesList.value && resendMessagesList.value.chatId === chat.value?.Id
		? resendMessagesList.value.messages
		: null
)

const answeredMessageHtml = computed(() => {
	const answered = attachOrResendMessages.value?.[0]
	if (!answered) {
		return ''
	}

	return applyChatMessagePlaceholders(answered.Text ?? '', answered.Links, answered.Bills)
		.replaceAll('\n', '<br>')
})

const toggleMessage = (message: ChatMessage, event?: MouseEvent) => {
	if ((event?.target as HTMLElement)?.closest('.link-message')) {
		return
	}
	if (props.mode === 'search-messages') {
		emit('on-open-message', message)
		return
	}

	if (selectedMessages.value.includes(message.Id)) {
		selectedMessages.value.splice(selectedMessages.value.indexOf(message.Id), 1)
	} else {
		selectedMessages.value.push(message.Id)
	}
}

const readMessage = (message: ChatMessage) => {
	if (message.Unreaded && worker.value?.Id !== message.WorkerId) {
		chatStore.readChatMessage(message.Id)
	}
}

const setMessageAnswered = (messageId: number) => {
	chatStore.setResendMessages({
		chatId: chat.value?.Id as number,
		messages: [chatMessages.value.find(message => message.Id === messageId)!]
	})
	focusAsync()
}

const onDropSelected = () => selectedMessages.value = []

const getDialogDate = (date: string) => moment(date).format("DD.MM.YYYY HH:mm");

const scrollToBottom = () => {
	const el = $messagesArea.value
	if (el) {
		el.scrollTop = el.scrollHeight
	}
}

const scrollDown = () => nextTick(scrollToBottom)

const scrollToPosition = (position: number) => nextTick(
	() => {
		$messagesArea.value ? $messagesArea.value.scrollTop = position : null
	}
)

const sendMessage = async () => {
	if (message.value && chat.value?.Id) {
		try {
			const messageText = message.value
			const attaches = JSON.parse(JSON.stringify(attachOrResendMessages.value))
			message.value = ''
			chatStore.setResendMessages(null)
			const lastMessageId = chatMessages.value[chatMessages.value.length - 1]?.Id
			await chatStore.createMessage({
				chatId: chat.value?.Id,
				text: messageText.replace(/\n{3,}/g, '\n\n'),
				attachedMessageIds: attaches
					? attaches.map(message => message.Id)
					: undefined,
			}, getMessageDate(), lastMessageId)
		} catch (e) {
			console.log(e)
		}
	}
}

const resendMessages = async () => {
	$resendMessages.value?.onOpen(chatMessages.value.filter(message => selectedMessages.value.includes(message.Id)))
}

const getMessageDate = () => {
	const date = new Date();

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	const hours = String(date.getHours()).padStart(2, '0');
	const minutes = String(date.getMinutes()).padStart(2, '0');
	const seconds = String(date.getSeconds()).padStart(2, '0');
	const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

	return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
}

const isAdjustingScroll = ref(false)
const paginationCooldownUntil = ref(0)

const preservePrependAnchor = async (action: () => Promise<void>) => {
	const el = $messagesArea.value
	if (!el) {
		await action();
		return
	}

	const prevHeight = el.scrollHeight
	const prevTop = el.scrollTop
	await action()
	await nextTick()
	await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
	if ($messagesArea.value) {
		isAdjustingScroll.value = true
		const newTop = prevTop + ($messagesArea.value.scrollHeight - prevHeight)
		$messagesArea.value.scrollTop = newTop
		currentChatScrollTopPosition.value = newTop
		requestAnimationFrame(() => { isAdjustingScroll.value = false })
	}
}

const loadPrev = async () => {
	if (!chat.value || !chatMessages.value.length) return

	if (chatStore.loadingPrev || chatStore.loadingNext) return

	if (!chatLoadingState.value?.canLoadPrev) return

	const chatId = chat.value.Id
	const date = chatMessages.value[0].Date
	await preservePrependAnchor(() => chatStore.getPrevMessages(chatId, date))
	paginationCooldownUntil.value = Date.now() + 250
}

const loadNext = async () => {
	if (!chat.value || !chatMessages.value.length) return

	if (chatStore.loadingPrev || chatStore.loadingNext) return

	if (!chatLoadingState.value?.canLoadNext) return

	await chatStore.getNextMessages(chat.value.Id, chatMessages.value[chatMessages.value.length - 1].Date)
	paginationCooldownUntil.value = Date.now() + 250
}

const loadPrevThrottled = debounce(loadPrev, 200, { leading: false, trailing: true })
const loadNextThrottled = debounce(loadNext, 200, { leading: false, trailing: true })

const updateChatMessages = () => {
	const el = $messagesArea.value
	if (!el || isAdjustingScroll.value || Date.now() < paginationCooldownUntil.value) return

	const scrollTop = el.scrollTop
	const prev = currentChatScrollTopPosition.value

	if (prev !== null && scrollTop < 500 && scrollTop < prev) {
		loadNextThrottled.cancel()
		loadPrevThrottled()
	} else if (prev !== null && scrollTop + el.offsetHeight + 500 > el.scrollHeight && scrollTop > prev) {
		loadPrevThrottled.cancel()
		loadNextThrottled()
	}

	currentChatScrollTopPosition.value = scrollTop
}

const setChatPosition = (scrollTop?: number) => {
	if (props.mode === 'chat') {
		if (scrollTop) {
			scrollToPosition(scrollTop)
		} else {
			const history = chatMessagesHistory.value.find(c => c.Id === chat.value?.Id)
			if (typeof history?.ScrollTop === 'number') {
				scrollToPosition(history.ScrollTop)
			} else {
				scrollDown()
			}
		}
	}
}

watch(chatMessages, () => {
	if (isFirstLoading.value) {
		if (!chatStore.pendingCenterMessageId) {
			setChatPosition()
		}

		isFirstLoading.value = false
	}
});

// Переменные чисто для отработки вотчеров
let prevChatId: number | null = null
let prevMessagesLen = 0
let prevLastMessageId: number | null = null

watch(chatMessages, (newMessages) => {
	const last = newMessages[newMessages.length - 1] ?? null
	const chatId = chatStore.getOpenedChatId

	const isAppend =
		isActiveMode.value &&
		chatId === prevChatId &&
		prevMessagesLen > 0 &&
		newMessages.length > prevMessagesLen &&
		last?.Id !== prevLastMessageId

	prevChatId = chatId
	prevMessagesLen = newMessages.length
	prevLastMessageId = last?.Id ?? null

	if (!isAppend) {
		return
	}

	if (worker.value?.Id === last?.WorkerId || !isScrollBtnVisible.value) {
		scrollToBottom()
	}
}, { flush: 'post' });

watch(() => chatStore.getOpenedChatId, (newId, oldId) => {
	if (!newId || newId === oldId || !isActiveMode.value) return
	if (!chatStore.pendingCenterMessageId) {
		setChatPosition()
	}

	focusAsync()
}, { flush: 'post' });

let scrollHandler: (() => void) | null = null

const centerOnTargetMessage = async () => {
	if (!chatStore.pendingCenterMessageId) return

	const targetId = chatStore.pendingCenterMessageId
	await nextTick()
	const tryScroll = (attempt = 0) => {
		const element = document.getElementById(`message-${targetId}`) as HTMLElement | null
		if (element) {
			element.scrollIntoView({ block: 'center', behavior: 'auto' })
			if ($messagesArea.value) currentChatScrollTopPosition.value = $messagesArea.value.scrollTop

			element.style.backgroundColor = '#D2D2D2'
			setTimeout(() => { element.style.backgroundColor = 'transparent' }, 1000)
			isFirstLoadingFromSearch.value = false
			chatStore.pendingCenterMessageId = null
			return
		}
		if (attempt < 15) {
			requestAnimationFrame(() => tryScroll(attempt + 1))
		} else {
			isFirstLoadingFromSearch.value = false
			chatStore.pendingCenterMessageId = null
		}
	}
	tryScroll()
}

watch(isLoadingChat, async (newVal) => {
	if (!newVal) {
		await nextTick()
		if (isInputFocused.value) await scrollToLatest()
		currentChatScrollTopPosition.value = $messagesArea.value?.scrollTop as number
		if (chatStore.pendingCenterMessageId) {
			await centerOnTargetMessage()
		}
	}
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 1)'
}))

onMounted(async () => {
	isFirstLoadingFromSearch.value = !!chatStore.pendingCenterMessageId

	if (isActiveMode.value) {
		focusAsync()
	}

	await nextTick()

	if (chat.value && chatMessages.value.length > 0) {
		if (chatStore.pendingCenterMessageId) {
			await centerOnTargetMessage()
		} else {
			setChatPosition()
		}
		isFirstLoading.value = false
		currentChatScrollTopPosition.value = $messagesArea.value?.scrollTop ?? null
	}

	prevChatId = chatStore.getOpenedChatId
	prevMessagesLen = chatMessages.value.length
	prevLastMessageId = chatMessages.value[chatMessages.value.length - 1]?.Id ?? null

	scrollHandler = () => {
		if (!$messagesArea.value) return
		isScrollBtnVisible.value =
			$messagesArea.value.scrollTop + $messagesArea.value.offsetHeight < ($messagesArea.value.scrollHeight - 50)

		updateChatMessages()

		if (chat.value) {
			chatStore.saveChatScrollPosition(chat.value.Id, $messagesArea.value.scrollTop)
		}
	}
	$messagesArea.value?.addEventListener('scroll', scrollHandler)
})


onBeforeUnmount(() => {
	loadPrevThrottled.cancel()
	loadNextThrottled.cancel()
	if (scrollHandler && $messagesArea.value) {
		$messagesArea.value.removeEventListener('scroll', scrollHandler)
		scrollHandler = null
	}
});

__expose({ focusSync, focusAsync })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(HeaderChatResendModal, {
      ref_key: "$resendMessages",
      ref: $resendMessages,
      title: _ctx.$t('chat_forward_select'),
      onOnResend: _cache[0] || (_cache[0] = ($event: any) => (selectedMessages.value = []))
    }, null, 8, ["title"]),
    _createVNode(BillInfoModal, {
      ref_key: "$billInfoModal",
      ref: $billInfoModal
    }, null, 512),
    _createElementVNode("div", {
      ref_key: "$chatPanel",
      ref: $chatPanel,
      class: "chat-messages-panel",
      style: _normalizeStyle(_unref(keyboardPanelStyle))
    }, [
      (isLoadingChat.value || isFirstLoadingFromSearch.value)
        ? (_openBlock(), _createBlock(AppLoader, {
            key: 0,
            style: _normalizeStyle(loaderStyle.value)
          }, null, 8, ["style"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: "chat-messages-panel__messages",
        ref_key: "$messagesArea",
        ref: $messagesArea
      }, [
        (selectedMessages.value.length)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                _createTextVNode(_toDisplayString(_ctx.$t('chat_selected_messages', { count: selectedMessages.value.length })) + " ", 1),
                _createVNode(AppIcon, {
                  name: "close-bold",
                  width: "12px",
                  color: "#D2D2D2",
                  "hover-color": "#4D4D4D",
                  cursor: "pointer",
                  style: { marginTop: '-2px' },
                  onClick: _withModifiers(onDropSelected, ["stop"])
                })
              ]),
              _createElementVNode("div", _hoisted_3, [
                _createVNode(AppIcon, {
                  class: "chat-messages-panel__messages-reply-btn-icon",
                  name: "reply-right",
                  width: "18px"
                }),
                _createElementVNode("span", {
                  class: "chat-messages-panel__messages-reply-btn-text",
                  onClick: resendMessages
                }, _toDisplayString(_ctx.$t('chat_forward')), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chatMessages.value, (message) => {
          return (_openBlock(), _createBlock(ChatMessageComponent, {
            key: message.Id,
            message: message,
            selected: selectedMessages.value.includes(message.Id),
            mode: props.mode,
            onClick: ($event: any) => (toggleMessage(message, $event)),
            onMouseenter: ($event: any) => (readMessage(message)),
            onSetAnswered: setMessageAnswered,
            onOpenBill: openBillInfo,
            onNavigated: _cache[1] || (_cache[1] = ($event: any) => (emit('on-navigate')))
          }, null, 8, ["message", "selected", "mode", "onClick", "onMouseenter"]))
        }), 128))
      ], 512),
      (isLoadingPrev.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(AppLoader)
          ]))
        : _createCommentVNode("", true),
      (isLoadingNext.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(AppLoader)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_6, [
        (isScrollBtnVisible.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "chat-messages-panel__scroll-btn",
              onClick: scrollDown
            }, [
              _createVNode(AppIcon, {
                name: "arrow-down",
                width: "24px",
                color: "#fff"
              })
            ]))
          : _createCommentVNode("", true),
        (attachOrResendMessages.value && props.mode === 'chat')
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (attachOrResendMessages.value.length === 1)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _cache[6] || (_cache[6] = _createElementVNode("div", { class: "answered-message__lable" }, null, -1)),
                    _createElementVNode("div", _hoisted_8, [
                      _createElementVNode("div", _hoisted_9, [
                        _createElementVNode("div", _hoisted_10, _toDisplayString(attachOrResendMessages.value[0].WorkerName), 1),
                        _createElementVNode("div", _hoisted_11, _toDisplayString(getDialogDate(attachOrResendMessages.value[0].Date)), 1),
                        _createVNode(AppIcon, {
                          class: "answered-message__body-header-close",
                          name: "close-bold",
                          width: "12px",
                          color: "#D2D2D2",
                          "hover-color": "#4D4D4D",
                          cursor: "pointer",
                          onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (_unref(chatStore).setResendMessages(null)), ["stop"]))
                        })
                      ]),
                      _createElementVNode("div", {
                        class: "answered-message__body-text",
                        innerHTML: answeredMessageHtml.value,
                        onClick: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(onMessageClick) && _unref(onMessageClick)(...args)))
                      }, null, 8, _hoisted_12)
                    ])
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                    _cache[7] || (_cache[7] = _createElementVNode("div", { class: "answered-message__lable" }, null, -1)),
                    _createElementVNode("div", _hoisted_14, [
                      _createElementVNode("div", _hoisted_15, [
                        _createVNode(AppIcon, {
                          class: "answered-message__body-header-close",
                          name: "close-bold",
                          width: "12px",
                          color: "#D2D2D2",
                          "hover-color": "#4D4D4D",
                          cursor: "pointer",
                          onClick: _cache[4] || (_cache[4] = _withModifiers(($event: any) => (_unref(chatStore).setResendMessages(null)), ["stop"]))
                        })
                      ]),
                      _createElementVNode("div", _hoisted_16, _toDisplayString(_ctx.$tc('chat_messages_count', attachOrResendMessages.value?.length ?? 0)), 1)
                    ])
                  ]))
            ], 64))
          : _createCommentVNode("", true),
        _withDirectives(_createElementVNode("div", _hoisted_17, [
          _createVNode(AppCustomTextarea, {
            ref_key: "inputRef",
            ref: inputRef,
            modelValue: message.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((message).value = $event)),
            placeholder: _ctx.$t('chat_message_placeholder'),
            "min-height": 32,
            "max-height": 100,
            "new-line-mode": workerSendMessageType.value?.id === 0 ? 'enter' : 'shift-enter',
            onFocus: _unref(onChatInputFocus),
            onBlur: _unref(onChatInputBlur),
            onEnter: sendMessage,
            onCtrlEnter: sendMessage
          }, null, 8, ["modelValue", "placeholder", "new-line-mode", "onFocus", "onBlur"]),
          _createVNode(AppTooltip, {
            class: "chat-search-panel__edit-btn",
            text: workerSendMessageType.value.sendMessageText
          }, {
            activator: _withCtx(() => [
              _createVNode(AppIcon, {
                class: "chat-messages-panel__input-field-btn",
                name: "send-message",
                width: "16px",
                color: message.value.trim().length ? '#3F884F' : '#D2D2D2',
                "hover-color": "#3F884F",
                cursor: "pointer",
                onClick: sendMessage
              }, null, 8, ["color"])
            ]),
            _: 1
          }, 8, ["text"])
        ], 512), [
          [_vShow, props.mode === 'chat']
        ])
      ])
    ], 4)
  ], 64))
}
}

})